import { useMemo, useRef, useState } from "react";
import { Badge, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { ControlledMenu, MenuItem } from "@szhsin/react-menu";
import "@szhsin/react-menu/dist/index.css";
import { useAuth } from "../../../providers/authprovider";
import { useNavigate } from "react-router-dom";
import StatusModal, { STATE_DENIED } from "../StatusModal";
import ProtocolsModal from "../ProtocolsModal";
import NotesModal from "../NotesModal";
import { useData } from "../Calendar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getUserInitials } from "../../../utils/utils";
import {
  faEllipsisVertical,
  faTriangleExclamation,
  faFileInvoiceDollar,
  faFileInvoice,
  faComment,
} from "@fortawesome/free-solid-svg-icons";
import AddEditEventModal from "../AddEditEventModal";
import { GetStateNamePl } from "../StatusModal";

function formatISODate(d) {
  if (!d) return "";
  const dt = typeof d === "string" ? new Date(d) : new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toISOString().slice(0, 10);
}

function getTypeName(type) {
  switch (type) {
    case "INSPECTION":
      return "Inspekcja";
    case "MALFUNCTION":
      return "Awaria/Zgłoszenie";
    case "TRAINING":
      return "Szkolenie";
    default:
      return String(type);
  }
}

function stateLabel(state) {
  if (!state) return "brak";
  return GetStateNamePl(state);
}

export default function CalendarEventsResultRow({
  event,
  privilege,
  onDelete,
  onEventChanged,
}) {
  const { user, calendar } = useAuth();
  const navigate = useNavigate();
  const { shortBuildings, teams, workers } = useData();

  const anchorRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showProtocolsModal, setShowProtocolsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);

  const isDenied = event.state === STATE_DENIED;
  const users = workers.filter(
    (w) => Array.isArray(event.users) && event.users.includes(w.id),
  );

  const canShowProtocols = Boolean(event.confirmed);
  const hasNotes = Number(event.hasNotes ?? 0) > 0;
  const hasOffers = Array.isArray(event.offers) && event.offers.length > 0;

  const handleOpenBuilding = (e) => {
    e?.stopPropagation?.();
    navigate(`/buildings/${event.building_id}`);
  };

  const handleDeleteEvent = (e) => {
    e?.stopPropagation?.();

    if (!window.confirm("Potwierdź usunięcie inspekcji/awarii z kalendarza."))
      return;

    closeMenu();

    calendar.remove(event.id, ({ success, content }) => {
      if (success === true) {
        // przekazuj id/event, żeby parent mógł usunąć
        onDelete?.({ id: event.id, event, content });
      }
    });
  };

  // helper: ujednolicamy payload zmiany (StatusModal / AddEdit)
  const emitChanged = (updatedEvent) => {
    if (!updatedEvent) return;
    onEventChanged?.(updatedEvent);
  };

  const openMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setMenuOpen(true);
  };

  const closeMenu = () => setMenuOpen(false);

  // klik w wiersz może w przyszłości robić np. podgląd
  const handleRowClick = () => {
    // na razie nic, żeby nie gryźć się z menu
  };

  const statusBadge = useMemo(() => {
    if (!event.state) return <Badge bg="secondary">brak</Badge>;
    if (isDenied)
      return (
        <Badge bg="danger">
          <FontAwesomeIcon icon={faTriangleExclamation} className="me-1" />
          {stateLabel(event.state)}
        </Badge>
      );
    return <Badge bg="primary">{stateLabel(event.state)}</Badge>;
  }, [event.state, isDenied]);

  return (
    <>
      <tr onClick={handleRowClick} style={{ cursor: "default" }}>
        <td className="text-nowrap">{formatISODate(event.date)}</td>
        <td className="text-nowrap">{event.time ?? "—"}</td>

        <td>
          <div className="fw-semibold">{event.name ?? "—"}</div>
          {event.buildingDescription ? (
            <div
              className="text-muted small text-truncate"
              style={{ maxWidth: 520 }}
            >
              {event.buildingDescription}
            </div>
          ) : null}
        </td>

        <td>
          <small className={"text-muted"}>
            <i>{event.info}</i>
          </small>
        </td>

        <td>
          <Badge bg="light" text="dark" className="border">
            {getTypeName(event.type) ?? "—"}
          </Badge>
        </td>

        <td>
          {event.inspectionName ? (
            <div className="text-muted small">{event.inspectionName}</div>
          ) : (
            <div className="text-muted small">—</div>
          )}
        </td>

        <td>{statusBadge}</td>

        <td>
          {users?.map((user) => (
              <small
                key={user.id}
                className={"userBadge"}
                style={{ backgroundColor: user.color }}
                title={user.name}
              >
                {getUserInitials(user)}
              </small>
            ))}

          <span className="ms-2">
            {hasNotes ? (
              <OverlayTrigger overlay={<Tooltip>Notatki</Tooltip>}>
                <FontAwesomeIcon icon={faComment} className="text-secondary" />
              </OverlayTrigger>
            ) : null}

            {hasOffers ? (
              <OverlayTrigger overlay={<Tooltip>Oferta</Tooltip>}>
                <FontAwesomeIcon
                  icon={faFileInvoice}
                  className="text-info ms-2"
                />
              </OverlayTrigger>
            ) : null}

            {event.fvat || event.fvat_submit ? (
              <OverlayTrigger
                overlay={<Tooltip>{event.fvat_description ?? "FVAT"}</Tooltip>}
              >
                <FontAwesomeIcon
                  icon={faFileInvoiceDollar}
                  className={`ms-2 ${event.fvat ? "text-success" : "text-muted"}`}
                />
              </OverlayTrigger>
            ) : null}
          </span>
        </td>

        <td className="text-end">
          <Button
            ref={anchorRef}
            variant="outline-secondary"
            size="sm"
            onClick={openMenu}
            onMouseDown={(e) => e.stopPropagation()}
            aria-label="menu"
          >
            <FontAwesomeIcon icon={faEllipsisVertical} />
          </Button>

          <ControlledMenu
            state={menuOpen ? "open" : "closed"}
            anchorRef={anchorRef}
            align="end"
            direction="bottom"
            arrow
            onClose={closeMenu}
            onMouseLeave={closeMenu}
          >
            {privilege?.edit ? (
              <MenuItem
                onClick={() => {
                  closeMenu();
                  setShowEditModal(true);
                }}
              >
                Edycja
              </MenuItem>
            ) : (
              <MenuItem
                onClick={() => {
                  closeMenu();
                  setShowEditModal(true);
                }}
              >
                Podgląd
              </MenuItem>
            )}

            <MenuItem
              onClick={() => {
                closeMenu();
                setShowStatusModal(true);
              }}
            >
              Status
            </MenuItem>

            {event.building_id ? (
              <MenuItem
                onClick={(e) => {
                  closeMenu();
                  handleOpenBuilding(e);
                }}
              >
                Obiekt
              </MenuItem>
            ) : null}

            {canShowProtocols ? (
              <MenuItem
                onClick={() => {
                  closeMenu();
                  setShowProtocolsModal(true);
                }}
              >
                Protokoły
              </MenuItem>
            ) : null}

            <MenuItem
              onClick={() => {
                closeMenu();
                setShowNotesModal(true);
              }}
            >
              Notatka
            </MenuItem>

            {user?.isAdmin ? (
              <MenuItem onClick={handleDeleteEvent}>Usuń</MenuItem>
            ) : null}
          </ControlledMenu>
        </td>
      </tr>

      {showStatusModal ? (
        <StatusModal
          show
          event={event}
          onHide={() => setShowStatusModal(false)}
          onChangeEvent={(changedPayload) => {
            emitChanged(changedPayload);
          }}
        />
      ) : null}

      {showEditModal && (
        <AddEditEventModal
          privilege={privilege}
          show={showEditModal}
          event={event}
          onHide={() => {
            setShowEditModal(false);
          }}
          onSuccess={(updated) => {
            emitChanged(updated);
            setShowEditModal(false);
          }}
          buildings={shortBuildings}
          teams={teams}
          workers={workers}
          date={event.date}
        />
      )}

      {showProtocolsModal ? (
        <ProtocolsModal
          show={showProtocolsModal}
          event={event}
          onHide={() => setShowProtocolsModal(false)}
        />
      ) : null}

      {showNotesModal ? (
        <NotesModal
          show
          event={event}
          onHide={() => setShowNotesModal(false)}
        />
      ) : null}
    </>
  );
}
